import { messaging, VAPID_KEY, firebaseConfig } from './config';
import { getToken, onMessage, MessagePayload } from "firebase/messaging";

import { toast } from 'react-toastify';
import { store } from '../../service';
import { notificationsApi } from '../../service/notifications/api';


class FirebaseMessagingService {
  private deviceToken: string | null = null;

  // Request notification permission
  async requestPermission(): Promise<boolean> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // console.log('Notification permission granted.');
        return true;
      } else {
        // console.log('Notification permission denied.');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Generate and get FCM token
  async getFCMToken(): Promise<string | null> {
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return null;
      }

      // Request permission if not already granted
      if (Notification.permission === 'default') {
        const granted = await this.requestPermission();
        if (!granted) return null;
      }

      if (Notification.permission !== 'granted') {
        console.warn('Notification permission not granted');
        return null;
      }

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
      });

      if (token) {
        // console.log('FCM Token generated:', token);
        this.deviceToken = token;
        return token;
      } else {
        console.warn('No registration token available.');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  // Get stored device token
  getDeviceToken(): string | null {
    return this.deviceToken;
  }

  // Initialize foreground message handler
  initializeForegroundMessages() {
    onMessage(messaging, (payload: any) => {

      // console.log('Message received in foreground:', payload);

      // Show notification using toast
      if (payload.notification) {
        toast.info(`${payload.notification.title}: ${payload.notification.body}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      
      // Refresh notifications list and unread count
      store.dispatch(notificationsApi.util.invalidateTags(['Notifications']));


      // You can also dispatch to Redux store if needed
      // store.dispatch(addNotification(payload));
    });
  }

  // Register service worker
  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const configParams = new URLSearchParams(firebaseConfig as Record<string, string>).toString();
        const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${configParams}`);
        // console.log('Service Worker registered successfully:', registration);

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available, notify user
                toast.info('New app update available! Refresh to update.', {
                  autoClose: false,
                  onClick: () => window.location.reload(),
                });
              }
            });
          }
        });

        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        return null;
      }
    } else {
      console.warn('Service Workers not supported');
      return null;
    }
  }

  // Initialize Firebase Messaging
  async initialize() {
    try {
      // Register service worker first
      await this.registerServiceWorker();

      // Initialize foreground message handler
      this.initializeForegroundMessages();

      // Get FCM token
      const token = await this.getFCMToken();
      return token;
    } catch (error) {
      console.error('Error initializing Firebase Messaging:', error);
      return null;
    }
  }

  // Handle token refresh
  onTokenRefresh(callback: (token: string) => void) {
    // Firebase automatically handles token refresh
    // We can listen to token changes if needed
    // For now, we'll rely on manual token generation when needed
  }
}

export const firebaseMessagingService = new FirebaseMessagingService();
export default firebaseMessagingService;